import React, { useEffect, useState } from 'react';
import { API, authFetch } from './api';
import { useAuth } from './AuthContext';

const Card = ({ title, children, right }) => (
  <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
    <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
      <h2 className="text-white font-semibold min-w-0 break-words">{title}</h2>{right}
    </div>
    {children}
  </div>
);
const In = ({ label, value, onChange, type = 'text', required }) => (
  <div>
    <label className="block text-xs text-[#B0BEC5] mb-1">{label}</label>
    <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm" />
  </div>
);
const CopyRow = ({ value }) => (
  <div className="flex gap-2 items-center">
    <input readOnly value={value} className="flex-1 min-w-0 px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-[#00D4FF] text-xs" />
    <button onClick={() => navigator.clipboard && navigator.clipboard.writeText(value)}
      className="px-3 py-1.5 text-sm bg-[#00D4FF] text-[#0A0E27] font-semibold rounded">Copy</button>
  </div>
);

function BrokerageManageRow({ aid, b, onChange }) {
  const { user } = useAuth();
  const canExtend = user?.role === 'super_admin'; // trial extensions are super-admin only
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ licensed_re: b.licensed_re, seats_re: b.seats_re, licensed_cre: b.licensed_cre, seats_cre: b.seats_cre });
  const [sub, setSub] = useState({ renewal_date: b.renewal_date || '', billing_frequency: b.billing_frequency || 'monthly' });
  const [msg, setMsg] = useState('');
  const patch = async (body) => {
    setMsg('');
    const res = await authFetch(`${API}/api/associations/${aid}/brokerages/${b.id}`, { method: 'PATCH', body: JSON.stringify(body) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(d.detail || 'Failed'); return; }
    onChange();
  };
  const extend = async () => {
    await authFetch(`${API}/api/associations/${aid}/brokerages/${b.id}/extend-trial`, { method: 'POST', body: JSON.stringify({ days: 14 }) });
    onChange();
  };
  return (
    <div className="py-2 border-b border-[#546E7A]/20 text-sm last:border-0">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <span className="text-white min-w-0 break-words">{b.name}{' '}
          <span className="text-gray-500 text-xs">{[b.licensed_re && `RE:${b.seats_re}`, b.licensed_cre && `CRE:${b.seats_cre}`].filter(Boolean).join(' · ')}</span>
        </span>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <span className="text-[#B0BEC5] text-xs">{b.in_trial ? `Trial ${b.trial_days_left}d → ${b.trial_end}` : 'Billable'} · {b.status}</span>
          {b.in_trial && canExtend && <button onClick={extend} className="px-2 py-0.5 text-xs bg-blue-600/80 text-white rounded">Extend +14d</button>}
          {b.status === 'active'
            ? <button onClick={() => patch({ status: 'suspended' })} className="px-2 py-0.5 text-xs bg-orange-600/80 text-white rounded">Suspend</button>
            : <button onClick={() => patch({ status: 'active' })} className="px-2 py-0.5 text-xs bg-[#00E676] text-[#0A0E27] rounded">Reactivate</button>}
          <button onClick={() => setOpen(!open)} className="px-2 py-0.5 text-xs text-[#00D4FF] hover:bg-[#243B55] rounded">{open ? 'Close' : 'Edit'}</button>
        </div>
      </div>
      {open && (
        <div className="mt-2 flex flex-wrap items-end gap-3 bg-[#0A0E27] rounded p-2 border border-[#546E7A]/30">
          <label className="flex items-center gap-1 text-xs text-white"><input type="checkbox" checked={f.licensed_re} onChange={(e) => setF({ ...f, licensed_re: e.target.checked })} /> RE</label>
          <input type="number" min="0" value={f.seats_re} disabled={!f.licensed_re} onChange={(e) => setF({ ...f, seats_re: e.target.value })} className="w-16 px-1 py-0.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white disabled:opacity-40" />
          <label className="flex items-center gap-1 text-xs text-white"><input type="checkbox" checked={f.licensed_cre} onChange={(e) => setF({ ...f, licensed_cre: e.target.checked })} /> CRE</label>
          <input type="number" min="0" value={f.seats_cre} disabled={!f.licensed_cre} onChange={(e) => setF({ ...f, seats_cre: e.target.value })} className="w-16 px-1 py-0.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white disabled:opacity-40" />
          <button onClick={() => patch({ licensed_re: f.licensed_re, seats_re: Number(f.seats_re) || 0, licensed_cre: f.licensed_cre, seats_cre: Number(f.seats_cre) || 0 })} className="px-3 py-1 text-xs bg-[#00D4FF] text-[#0A0E27] font-semibold rounded">Save</button>
          {msg && <span className="text-red-400 text-xs">{msg}</span>}
          <div className="w-full flex flex-wrap items-end gap-3 border-t border-[#546E7A]/20 pt-2 mt-1">
            <label className="text-xs text-[#B0BEC5]">Renewal date
              <input type="date" value={sub.renewal_date} onChange={(e) => setSub({ ...sub, renewal_date: e.target.value })} className="block mt-0.5 px-1 py-0.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white" /></label>
            <label className="text-xs text-[#B0BEC5]">Billing
              <select value={sub.billing_frequency} onChange={(e) => setSub({ ...sub, billing_frequency: e.target.value })} className="block mt-0.5 px-1 py-1 rounded bg-[#1A2744] border border-[#546E7A]/40 text-white">
                <option value="monthly">Monthly</option><option value="annual">Annual</option></select></label>
            <button onClick={() => patch({ renewal_date: sub.renewal_date, billing_frequency: sub.billing_frequency })} className="px-3 py-1 text-xs bg-[#00D4FF] text-[#0A0E27] font-semibold rounded">Save subscription</button>
          </div>
        </div>
      )}
    </div>
  );
}

function SeatRequestsCard({ aid }) {
  const [reqs, setReqs] = useState([]);
  const load = () => authFetch(`${API}/api/associations/${aid}/seat-requests?status=pending`).then((r) => (r.ok ? r.json() : [])).then(setReqs);
  useEffect(() => { load(); }, [aid]);
  const decide = async (id, action) => { await authFetch(`${API}/api/associations/${aid}/seat-requests/${id}/${action}`, { method: 'POST' }); load(); };
  return (
    <Card title={`Seat requests (${reqs.length})`}>
      {reqs.length === 0 && <p className="text-[#B0BEC5] text-sm">No pending seat requests.</p>}
      {reqs.map((r) => (
        <div key={r.id} className="flex flex-wrap justify-between items-center gap-2 py-2 border-b border-[#546E7A]/20 text-sm last:border-0">
          <span className="text-white min-w-0 break-words">{r.brokerage_name} — {String(r.product).toUpperCase()} &rarr; {r.requested_seats} seats</span>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => decide(r.id, 'approve')} className="px-2 py-1 text-xs bg-[#00E676] text-[#0A0E27] font-semibold rounded">Approve</button>
            <button onClick={() => decide(r.id, 'reject')} className="px-2 py-1 text-xs bg-red-600/80 text-white rounded">Reject</button>
          </div>
        </div>
      ))}
    </Card>
  );
}


function TrialsEndingCard({ aid }) {
  const { user } = useAuth();
  const canExtend = user?.role === 'super_admin'; // trial extensions are super-admin only
  const [rows, setRows] = useState([]);
  const load = () => authFetch(`${API}/api/associations/${aid}/trials-ending?within=7`).then((r) => (r.ok ? r.json() : [])).then(setRows);
  useEffect(() => { load(); }, [aid]);
  const extend = async (bid) => { await authFetch(`${API}/api/associations/${aid}/brokerages/${bid}/extend-trial`, { method: 'POST', body: JSON.stringify({ days: 14 }) }); load(); };
  return (
    <Card title={`Trials ending this week (${rows.length})`}>
      {rows.length === 0 && <p className="text-[#B0BEC5] text-sm">No trials ending in the next 7 days.</p>}
      {rows.map((b) => (
        <div key={b.id} className="flex flex-wrap justify-between items-center gap-2 py-2 border-b border-[#546E7A]/20 text-sm last:border-0">
          <span className="text-white min-w-0 break-words">{b.name} — {b.trial_days_left} day{b.trial_days_left !== 1 ? 's' : ''} left (ends {b.trial_end})</span>
          {canExtend && <button onClick={() => extend(b.id)} className="px-2 py-1 text-xs bg-blue-600/80 text-white rounded shrink-0">Extend +14d</button>}
        </div>
      ))}
    </Card>
  );
}


// Association console section: shareable signup link, approval queue (approve/adjust/
// mass-approve/reject), manual create, and the list of provisioned brokerages.
export default function AssociationBrokerages({ assoc }) {
  const aid = assoc.id;
  const [signups, setSignups] = useState([]);
  const [brokerages, setBrokerages] = useState([]);
  const [sel, setSel] = useState({});
  const [invites, setInvites] = useState([]);
  const [err, setErr] = useState('');
  const link = `${window.location.origin}/signup-brokerage/${assoc.signup_slug}`;

  const load = () => {
    authFetch(`${API}/api/associations/${aid}/signups?status=pending`).then((r) => (r.ok ? r.json() : [])).then(setSignups);
    authFetch(`${API}/api/associations/${aid}/brokerages`).then((r) => (r.ok ? r.json() : [])).then(setBrokerages);
  };
  useEffect(load, [aid]);

  const addInvite = (name, path) => setInvites((v) => [{ name, link: `${window.location.origin}${path}` }, ...v]);

  const approve = async (s, adj) => {
    setErr('');
    const res = await authFetch(`${API}/api/associations/${aid}/signups/${s.id}/approve`, { method: 'POST', body: JSON.stringify(adj || {}) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setErr(data.detail || 'Approve failed'); return; }
    addInvite(s.name, data.invite_path); load();
  };
  const reject = async (s) => { await authFetch(`${API}/api/associations/${aid}/signups/${s.id}/reject`, { method: 'POST' }); load(); };
  const massApprove = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]).map(Number);
    if (!ids.length) return;
    const res = await authFetch(`${API}/api/associations/${aid}/signups/approve-batch`, { method: 'POST', body: JSON.stringify({ signup_ids: ids }) });
    const data = await res.json().catch(() => ({}));
    (data.results || []).forEach((r) => {
      if (r.ok) { const s = signups.find((x) => x.id === r.signup_id); addInvite(s ? s.name : ('#' + r.signup_id), r.invite_path); }
    });
    setSel({}); load();
  };

  return (
    <div className="space-y-4">
      <Card title="Brokerage signup link">
        <p className="text-xs text-[#B0BEC5] mb-2">Share this with your membership. Submissions land in the approval queue below — nothing is provisioned until you approve.</p>
        <CopyRow value={link} />
      </Card>

      <Card title={`Approval queue (${signups.length})`}
        right={signups.length > 0 && <button onClick={massApprove} className="px-3 py-1.5 text-sm bg-[#00E676] text-[#0A0E27] font-semibold rounded">Approve selected</button>}>
        {err && <p className="text-red-400 text-sm mb-2">{err}</p>}
        {signups.length === 0 && <p className="text-[#B0BEC5] text-sm">No pending signups.</p>}
        {signups.map((s) => <SignupRow key={s.id} s={s} assoc={assoc} checked={!!sel[s.id]} onCheck={(v) => setSel({ ...sel, [s.id]: v })} onApprove={approve} onReject={reject} />)}
      </Card>

      {invites.length > 0 && (
        <Card title="Broker invite links (email is stubbed — send these)">
          {invites.map((iv, i) => (
            <div key={i} className="mb-2">
              <p className="text-xs text-[#B0BEC5] mb-1">{iv.name}</p>
              <CopyRow value={iv.link} />
            </div>
          ))}
        </Card>
      )}

      <ManualCreate assoc={assoc} onCreated={(name, path) => { addInvite(name, path); load(); }} />

      <SeatRequestsCard aid={aid} />

      <TrialsEndingCard aid={aid} />

      <Card title={`Brokerages (${brokerages.length})`}>
        {brokerages.length === 0 && <p className="text-[#B0BEC5] text-sm">No brokerages yet.</p>}
        {brokerages.map((b) => <BrokerageManageRow key={b.id} aid={aid} b={b} onChange={load} />)}
      </Card>
    </div>
  );
}

function SignupRow({ s, assoc, checked, onCheck, onApprove, onReject }) {
  const [wRe, setRe] = useState(s.want_re); const [sRe, setSre] = useState(s.seats_re);
  const [wCre, setCre] = useState(s.want_cre); const [sCre, setScre] = useState(s.seats_cre);
  return (
    <div className="py-2 border-b border-[#546E7A]/20 last:border-0">
      <div className="flex items-start gap-2">
        <input type="checkbox" checked={checked} onChange={(e) => onCheck(e.target.checked)} className="mt-1" />
        <div className="flex-1">
          <div className="text-white text-sm font-medium">{s.name} <span className="text-gray-500">· {s.broker_name} ({s.broker_email})</span></div>
          <div className="flex flex-wrap gap-3 mt-1 items-center">
            {assoc.licensed_re && <label className="flex items-center gap-1 text-xs text-white"><input type="checkbox" checked={wRe} onChange={(e) => setRe(e.target.checked)} /> RE <input type="number" min="0" value={sRe} disabled={!wRe} onChange={(e) => setSre(e.target.value)} className="w-16 px-1 py-0.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white disabled:opacity-40" /></label>}
            {assoc.licensed_cre && <label className="flex items-center gap-1 text-xs text-white"><input type="checkbox" checked={wCre} onChange={(e) => setCre(e.target.checked)} /> CRE <input type="number" min="0" value={sCre} disabled={!wCre} onChange={(e) => setScre(e.target.value)} className="w-16 px-1 py-0.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white disabled:opacity-40" /></label>}
            <button onClick={() => onApprove(s, { want_re: wRe, seats_re: Number(sRe) || 0, want_cre: wCre, seats_cre: Number(sCre) || 0 })} className="px-2 py-1 text-xs bg-[#00E676] text-[#0A0E27] font-semibold rounded">Approve</button>
            <button onClick={() => onReject(s)} className="px-2 py-1 text-xs bg-red-600/80 text-white rounded">Reject</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManualCreate({ assoc, onCreated }) {
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState('');
  const [f, setF] = useState({
    name: '', broker_name: '', broker_email: '', broker_phone: '',
    want_re: assoc.licensed_re && !assoc.licensed_cre, seats_re: 1,
    want_cre: assoc.licensed_cre && !assoc.licensed_re, seats_cre: 1,
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  const create = async (e) => {
    e.preventDefault(); setErr('');
    if (!(f.want_re || f.want_cre)) { setErr('Select at least one product.'); return; }
    const body = { ...f, seats_re: f.want_re ? Number(f.seats_re) || 0 : 0, seats_cre: f.want_cre ? Number(f.seats_cre) || 0 : 0 };
    const res = await authFetch(`${API}/api/associations/${assoc.id}/brokerages`, { method: 'POST', body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setErr(data.detail || 'Create failed'); return; }
    onCreated(f.name, data.invite_path);
    setF({ ...f, name: '', broker_name: '', broker_email: '', broker_phone: '' }); setOpen(false);
  };
  return (
    <Card title="Add a brokerage manually" right={<button onClick={() => setOpen(!open)} className="px-3 py-1.5 text-sm text-[#00D4FF] hover:bg-[#243B55] rounded">{open ? 'Close' : 'New'}</button>}>
      {!open ? <p className="text-[#B0BEC5] text-sm">For phone/handshake signups — provisions immediately and starts the trial.</p> : (
        <form onSubmit={create} className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <In label="Brokerage name *" value={f.name} onChange={(v) => set('name', v)} required />
            <In label="Principal broker name *" value={f.broker_name} onChange={(v) => set('broker_name', v)} required />
            <In label="Broker email *" type="email" value={f.broker_email} onChange={(v) => set('broker_email', v)} required />
            <In label="Broker phone" value={f.broker_phone} onChange={(v) => set('broker_phone', v)} />
          </div>
          <div className="flex flex-wrap gap-4">
            {assoc.licensed_re && <label className="flex items-center gap-1 text-sm text-white"><input type="checkbox" checked={f.want_re} onChange={(e) => set('want_re', e.target.checked)} /> RE seats <input type="number" min="0" value={f.seats_re} disabled={!f.want_re} onChange={(e) => set('seats_re', e.target.value)} className="w-16 px-1 py-0.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white disabled:opacity-40" /></label>}
            {assoc.licensed_cre && <label className="flex items-center gap-1 text-sm text-white"><input type="checkbox" checked={f.want_cre} onChange={(e) => set('want_cre', e.target.checked)} /> CRE seats <input type="number" min="0" value={f.seats_cre} disabled={!f.want_cre} onChange={(e) => set('seats_cre', e.target.value)} className="w-16 px-1 py-0.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white disabled:opacity-40" /></label>}
          </div>
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <button type="submit" className="px-4 py-2 rounded-md bg-[#00E676] text-[#0A0E27] font-semibold">Create &amp; Invite</button>
        </form>
      )}
    </Card>
  );
}
